class RangeSlider extends window.BAO.CustomElement() {
  static get requiredAttributes () {
    return ['min', 'max']
  }

  static get requiredElements () {
    return ['touchLeft', 'touchRight', 'line']
  }

  connectedCallback () {
    super.connectedCallback()

    this.selectedTouchEl = null

    this.syncVisualFromValues()
    this._commitSliderLayout()
    this.setAttribute('data-ready', '')
  }

  disconnectedCallback () {
    if (this._positionRafId != null) {
      cancelAnimationFrame(this._positionRafId)
      this._positionRafId = null
    }
    if (this._resizeRoRafId != null) {
      cancelAnimationFrame(this._resizeRoRafId)
      this._resizeRoRafId = null
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = undefined
    }
    super.disconnectedCallback?.()
  }

  setupListeners () {
    super.setupListeners()

    this.onStart = this.onStart.bind(this)
    this.onStop = this.onStop.bind(this)
    this.onMove = this.onMove.bind(this)
    this.setTouchElPositions = this.setTouchElPositions.bind(this)
    this.reset = this.reset.bind(this)

    this.listeners.add(
      this.externalEls.trigger.element,
      'click',
      this.setTouchElPositions,
    )

    this.listeners.add(this.touchLeftEl, 'mousedown', this.onStart)
    this.listeners.add(this.touchRightEl, 'mousedown', this.onStart)
    this.listeners.add(this.touchLeftEl, 'touchstart', this.onStart)
    this.listeners.add(this.touchRightEl, 'touchstart', this.onStart)

    this.onResize = this.onResize.bind(this)
    this.debouncedResize = window.BAO.utils.debounce(event => {
      this.onResize(event)
    }, 240)
    this.listeners.add(window, 'resize', this.debouncedResize)

    this.closest('details')?.addEventListener('toggle', event => {
      if (event.target.open) {
        this.setTouchElPositions()
      }
    })

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        if (this._resizeRoRafId != null) {
          cancelAnimationFrame(this._resizeRoRafId)
        }
        this._resizeRoRafId = requestAnimationFrame(() => {
          this._resizeRoRafId = null
          this.syncVisualFromValues()
        })
      })
      this.resizeObserver.observe(this)
    }
  }

  reset () {
    this._minValue = undefined
    this._maxValue = undefined
    this.removeAttribute('min-value')
    this.removeAttribute('max-value')

    this.els.minValue.element.innerText = this.formatMoney(this.min)
    this.els.maxValue.element.innerText = this.formatMoney(this.max)

    this.touchLeftEl.style.left = ''
    this.touchRightEl.style.left = ''
    this.touchRightEl.style.right = ''

    const highlighterEl = this.els.highlighter?.element
    if (highlighterEl) {
      highlighterEl.style = ''
    }

    this.syncVisualFromValues()
  }

  onResize () {
    this.setTouchElPositions()
  }

  onStart (event) {
    if (event?.button && event?.button !== 0) return
    if (this.isPriceRangeLocked()) return

    event.preventDefault()
    this.eventTouch = event.touches ? event.touches[0] : event

    this.selectedTouchEl =
      event.target.closest(this.els.touchLeft.selector) ||
      event.target.closest(this.els.touchRight.selector)

    document.addEventListener('mousemove', this.onMove)
    document.addEventListener('mouseup', this.onStop)
    document.addEventListener('touchmove', this.onMove)
    document.addEventListener('touchend', this.onStop)
  }

  onStop () {
    document.removeEventListener('mousemove', this.onMove)
    document.removeEventListener('mouseup', this.onStop)
    document.removeEventListener('touchmove', this.onMove)
    document.removeEventListener('touchend', this.onStop)

    this.selectedTouchEl = null
  }

  onMove (event) {
    if (this.isPriceRangeLocked()) return

    this.eventTouch = event.touches ? event.touches[0] : event
    const lineEl = this.els.line.element
    const rect = lineEl.getBoundingClientRect()
    const w = rect.width || 1
    let ratio = (this.eventTouch.clientX - rect.left) / w
    ratio = Math.max(0, Math.min(1, ratio))

    const span = Math.max(this.max - this.min, 1e-9)
    const valueAtPointer = this.min + ratio * span

    if (this.selectedTouchEl.matches(this.els.touchLeft.selector)) {
      const capped = Math.min(Math.floor(valueAtPointer), this.maxValue)
      this.minValue = Math.max(this.min, capped)
      this.syncVisualFromValues()
    }

    if (this.selectedTouchEl.matches(this.els.touchRight.selector)) {
      const capped = Math.max(Math.ceil(valueAtPointer), this.minValue)
      this.maxValue = Math.min(this.max, capped)
      this.syncVisualFromValues()
      this.updateSliderClasses()
    }
  }

  _commitSliderLayout () {
    this.els.minValue.element.innerText = this.formatMoney(this.minValue)
    this.els.maxValue.element.innerText = this.formatMoney(this.maxValue)
    this.syncVisualFromValues()
    this.updateSliderClasses()
  }

  setTouchElPositions () {
    if (this._positionRafId != null) {
      cancelAnimationFrame(this._positionRafId)
    }
    this._positionRafId = requestAnimationFrame(() => {
      this._positionRafId = null
      this._commitSliderLayout()
      this.setAttribute('data-ready', '')
    })
  }

  /**
   * Maps current min/max values to 0–1 fractions and drives layout via CSS
   * variables so thumbs and highlighter stay inside the slider on resize.
   */
  syncVisualFromValues () {
    const rangeMin = this.min
    const rangeMax = this.max

    if (!Number.isFinite(rangeMin) || !Number.isFinite(rangeMax)) return

    if (this.isPriceRangeLocked()) {
      this.setAttribute('data-range-locked', '')
      this.setAttribute('aria-disabled', 'true')
      this.style.setProperty('--rng-min-frac', '0')
      this.style.setProperty('--rng-max-frac', '1')
      const v = rangeMin
      this.els.minValue.element.innerText = this.formatMoney(v)
      this.els.maxValue.element.innerText = this.formatMoney(v)
      this.touchLeftEl.disabled = true
      this.touchRightEl.disabled = true
      return
    }

    this.removeAttribute('data-range-locked')
    this.removeAttribute('aria-disabled')
    this.touchLeftEl.disabled = false
    this.touchRightEl.disabled = false

    const span = Math.max(rangeMax - rangeMin, 1e-9)

    let vMin = this.minValue
    let vMax = this.maxValue
    vMin = Math.max(rangeMin, Math.min(vMin, rangeMax))
    vMax = Math.max(rangeMin, Math.min(vMax, rangeMax))
    if (vMin > vMax) {
      const t = vMin
      vMin = vMax
      vMax = t
    }

    let minF = (vMin - rangeMin) / span
    let maxF = (vMax - rangeMin) / span
    minF = Math.max(0, Math.min(1, minF))
    maxF = Math.max(0, Math.min(1, maxF))
    maxF = Math.max(minF, maxF)
    if (maxF <= minF) {
      maxF = Math.min(1, minF + 0.001)
    }

    this.style.setProperty('--rng-min-frac', String(minF))
    this.style.setProperty('--rng-max-frac', String(maxF))
  }

  isPriceRangeLocked () {
    const lo = this.min
    const hi = this.max
    return Number.isFinite(lo) && Number.isFinite(hi) && hi <= lo
  }

  get min () {
    return this.hasAttribute('min') ? parseFloat(this.getAttribute('min')) : 0
  }

  get max () {
    return this.hasAttribute('max') ? parseFloat(this.getAttribute('max')) : 100
  }

  set minValue (value) {
    this.setAttribute('min-value', value)
    this.els.minValue.element.innerText = this.formatMoney(value)

    if (this.minToUpdateEl) {
      this.minToUpdateEl.value = value
      this.minToUpdateEl.dispatchEvent(new Event('input', { bubbles: true }))
    }

    this._minValue = value
  }

  get minValue () {
    if (this._minValue === undefined) {
      return this.hasAttribute('min-value')
        ? parseFloat(this.getAttribute('min-value'))
        : this.min
    }

    return this._minValue
  }

  set maxValue (value) {
    this.setAttribute('max-value', value)
    this.els.maxValue.element.innerText = this.formatMoney(value)

    if (this.maxToUpdateEl) {
      this.maxToUpdateEl.value = value
      this.maxToUpdateEl.dispatchEvent(new Event('input', { bubbles: true }))
    }

    this._maxValue = value
  }

  get maxValue () {
    if (this._maxValue === undefined) {
      return this.hasAttribute('max-value')
        ? parseFloat(this.getAttribute('max-value'))
        : this.max
    }

    return this._maxValue
  }

  get defaultMinValue () {
    const tmpMin = this.minValue >= this.min ? this.minValue : this.min
    return tmpMin > this.defaultMaxValue ? this.defaultMaxValue : tmpMin
  }

  get defaultMaxValue () {
    return this.maxValue >= this.max ? this.maxValue : this.max
  }

  get touchLeftEl () {
    return this.els.touchLeft.element
  }

  get touchRightEl () {
    return this.els.touchRight.element
  }

  get minToUpdateEl () {
    if (
      !this.externalEls?.container?.exists ||
      !this.externalEls?.min?.exists
    ) {
      return
    }
    if (this.closest(this.externalEls.container.selector)) {
      const containerEl = this.closest(this.externalEls.container.selector)
      return window.BAO.utils.getElement(this.externalEls.min.selector, {
        context: containerEl,
      })
    }
  }

  get maxToUpdateEl () {
    if (
      !this.externalEls?.container?.exists ||
      !this.externalEls?.max?.exists
    ) {
      return
    }
    if (this.closest(this.externalEls.container.selector)) {
      const containerEl = this.closest(this.externalEls.container.selector)
      return window.BAO.utils.getElement(this.externalEls.max.selector, {
        context: containerEl,
      })
    }
  }

  formatMoney (value) {
    return `${window.BAO.utils.formatMoney()[0]}${value}`
  }

  updateSliderClasses () {
    const atMax = this.maxValue >= this.max
    this.classList.toggle('at-max', atMax)
  }
}

if (!customElements.get('range-slider')) {
  customElements.define('range-slider', RangeSlider)
}
